import Card from "@material-ui/core/Card";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { useRef, useState } from "react";
import { changeAppState } from "../../../../../../../../../../common/appState";
import { fetchPost } from "../../../../../../../../../../common/util/fetch";
import ShurikenProgress from "../../../../../../../../../shared/Animations/ShurikenProgress";
import { DarkButton } from "../../../../../../../../../shared/Button/DarkButton";
import { PrimaryButton } from "../../../../../../../../../shared/Button/PrimaryButton";
import { EllipsisLabel } from "../../../../../../../../../shared/EllipsisLabel/EllipsisLabel";
import { DarkPopoverMenu } from "../../../../../../../../../shared/Popover/DarkPopoverMenu";
import { openOtherUserPanel } from "../../../../../../../../../shared/User/OtherUserPanel/functions";
import { UserAvatar } from "../../../../../../../../../shared/User/UserAvatar/UserAvatar";
import {
    RelatedUser,
    User,
    UserForRanking,
} from "../../../../../../../../../shared/User/types";

export function RequestRecord({
    friend,
    colorNumber,
    user,
    scrollableContainerEl,
}: {
    friend: UserForRanking;
    user: User;
    colorNumber: number;
    scrollableContainerEl: HTMLDivElement | null;
}) {
    const c = useStyles();

    const openUserPanel = () => {
        openOtherUserPanel(friend.userId);
    };

    const [submitting, setSubmitting] = useState(false);

    return (
        <Card className={c.card}>
            <div className={c.avatarContainer} onClick={openUserPanel}>
                <UserAvatar user={friend} colorNumber={colorNumber} size={40} />
            </div>
            <div className={c.nameAndButtons}>
                <div className={c.name} onClick={openUserPanel}>
                    <EllipsisLabel title={friend.name} placement="top" />
                </div>
                <div className={c.buttons}>
                    <PrimaryButton
                        className={c.messageButton}
                        onClick={async () => {
                            setSubmitting(true);
                            await fetchApprove(user.userId, friend.userId);
                            setSubmitting(false);
                        }}
                        disabled={submitting}
                    >
                        {submitting ? (
                            <ShurikenProgress size={15} />
                        ) : (
                            "Confirm"
                        )}
                    </PrimaryButton>
                    <DeleteIconButton
                        className={c.iconButton}
                        containerEl={scrollableContainerEl}
                        friend={friend}
                        user={user}
                        submitting={submitting}
                        setSubmitting={setSubmitting}
                    />
                </div>
            </div>
        </Card>
    );
}
const useStyles = makeStyles({
    card: {
        margin: 5,
        display: "flex",
        padding: 5,
        height: 50,
        justifyContent: "center",
        width: "calc(100% - 10px)",
    },
    avatarContainer: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginRight: 10,
        minWidth: 35,
        cursor: "pointer",
    },
    nameAndButtons: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-around",
        width: "calc(100% - 65px)",
        height: "100%",
    },
    name: {
        width: "100%",
        paddingRight: 10,
        maxWidth: "100%",
        overflow: "hidden",
        cursor: "pointer",
    },
    buttons: {
        width: 96,
        display: "flex",
        justifyContent: "space-between",
        height: "100%",
        alignItems: "center",
    },
    iconButton: {
        minWidth: 30,
        minHeight: 30,
        maxWidth: 30,
        maxHeight: 30,
        margin: 5,
        lineHeight: 1,
    },
    messageButton: {
        minWidth: 66,
        minHeight: 30,
        maxWidth: 66,
        maxHeight: 30,
        fontSize: "small",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
});

function DeleteIconButton({
    className,
    containerEl,
    friend,
    user,
    submitting,
    setSubmitting,
}: {
    className: string;
    containerEl: HTMLDivElement | null;
    friend: UserForRanking;
    user: User;
    submitting: boolean;
    setSubmitting: (submitting: boolean) => void;
}) {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const [isPopoverOpen, setPopoverOpen] = useState(false);

    return (
        <>
            <DarkButton
                ref={buttonRef}
                className={className}
                onClick={() => {
                    setPopoverOpen(!isPopoverOpen);
                }}
            >
                <DeleteIcon />
            </DarkButton>
            <DarkPopoverMenu
                anchorEl={buttonRef.current}
                containerEl={containerEl}
                open={!!buttonRef.current && isPopoverOpen}
                onClose={() => {
                    setPopoverOpen(false);
                }}
                items={[
                    {
                        label: "Delete Request",
                        icon: <DeleteIcon />,
                        onClick: async () => {
                            setSubmitting(true);
                            await fetchDecline(user.userId, friend.userId);
                            setSubmitting(false);
                        },
                    },
                ]}
                disabled={submitting}
            />
        </>
    );
}

async function fetchApprove(myUserId: number, requesterUserId: number) {
    const result = await fetchPost<
        { myUserId: number; requesterUserId: number },
        {
            friendsLimit: "ok" | "MyFriendsLimit" | "TheirFriendsLimit";
            relatedUsers: RelatedUser[];
        }
    >("api/Friend/ApproveFriendRequest", { myUserId, requesterUserId });

    if ("error" in result) {
        return;
    }
    changeAppState("relatedUsers", result.relatedUsers);

    switch (result.friendsLimit) {
        case "MyFriendsLimit": {
            changeAppState("friendsLimitDialogState", { type: "myLimit" });
            break;
        }
        case "TheirFriendsLimit": {
            changeAppState("friendsLimitDialogState", {
                type: "theirLimit",
                targetUserId: requesterUserId,
            });
            break;
        }
    }
}

async function fetchDecline(myUserId: number, requesterUserId: number) {
    const relatedUsers = await fetchPost<
        { myUserId: number; requesterUserId: number },
        RelatedUser[]
    >("api/Friend/DeclineFriendRequest", { myUserId, requesterUserId });

    if ("error" in relatedUsers) {
        return;
    }
    changeAppState("relatedUsers", relatedUsers);
}
